import "./sentry.ts";
import * as THREE from "three";
import { api } from "./api/client.ts";
import type { CameraMode, Measurement, MeasurementType, Note, NoteCategory, Project, RoomPlanData } from "./types.ts";
import type { IcRoomPlanJson } from "./ui/RoomPlanJson2D.ts";
import { Viewer3D } from "./viewer/Viewer3D.ts";
import type { ClipAxis } from "./viewer/Viewer3D.ts";
import { MeasurePanel } from "./ui/MeasurePanel.ts";
import { NotePanel } from "./ui/NotePanel.ts";
import { InspectorPanel } from "./ui/InspectorPanel.ts";
import { SidePanel } from "./ui/SidePanel.ts";
import { IcProjectsPage } from "./ui/IcProjectsPage.ts";
import { IcProjectDetailPage } from "./ui/IcProjectDetailPage.ts";
import { LoginPage } from "./ui/LoginPage.ts";
import { clearToken, getToken } from "./auth.ts";

// ── DOM refs ──────────────────────────────────────────────────────────────────
// Phase-1+ elements are looked up without `!` so a stale-cached index.html
// (missing the new IC nodes) doesn't take down the entire bootstrap chain
// before the user can hard-refresh. `requireEl` logs once and returns a
// detached fallback so listener-binding remains safe.
function requireEl<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id) as T | null;
  if (el) return el;
  console.warn(`[main] #${id} not found in DOM — UI for this control is disabled. ` +
    "Hard-refresh the page if you've recently updated the bundle.");
  // Detached placeholder lets `.classList`, `.addEventListener`, `.style.display = …`
  // calls succeed silently rather than crashing app init.
  return document.createElement("div") as unknown as T;
}

const pageIc = requireEl("page-ic-projects");
const pageIcDetail = requireEl("page-ic-detail");
const pageViewer = document.getElementById("page-viewer")!;

const btnIcProjects = requireEl("btn-ic-projects");
const btnLogout = requireEl("btn-logout");
// Top-toolbar slots populated by IC detail mode — back button and the
// centered project title. Kept in #toolbar so the IC detail screen can
// drop its own internal header (saves ~52px of vertical space).
const btnIcDetailBack = document.getElementById("btn-ic-detail-back")!;
const icDetailTitle = document.getElementById("ic-detail-title")!;

const viewerBack = document.getElementById("viewer-back")!;
const loadingOverlay = document.getElementById("loading-overlay")!;
const loadingText = document.getElementById("loading-text")!;
const canvasContainer = document.getElementById("canvas-container")!;

// Viewer toolbar
const modeDollhouse = document.getElementById("mode-dollhouse")!;
const modeWalkthrough = document.getElementById("mode-walkthrough")!;
const modeFloorplan = document.getElementById("mode-floorplan")!;
const btnMeasure = document.getElementById("btn-measure")!;
const btnNotes = document.getElementById("btn-notes")!;

// Measure sub-toolbar
const measureToolbar = document.getElementById("measure-toolbar")!;
const measureHint = document.getElementById("measure-hint")!;
const mDistance = document.getElementById("m-distance")!;
const mHeight = document.getElementById("m-height")!;
const mArea = document.getElementById("m-area")!;
const mPoint = document.getElementById("m-point")!;

// Notes sub-toolbar
const notesToolbar = document.getElementById("notes-toolbar")!;
const notesHint = document.getElementById("notes-hint")!;
const nRoom = document.getElementById("n-room")!;
const nNote = document.getElementById("n-note")!;
const nDamage = document.getElementById("n-damage")!;

// Clip plane controls
const btnClip = document.getElementById("btn-clip")!;
const clipToolbar = document.getElementById("clip-toolbar")!;
const clipX = document.getElementById("clip-x")!;
const clipY = document.getElementById("clip-y")!;
const clipZ = document.getElementById("clip-z")!;

// Plan mode controls
const planControls = document.getElementById("plan-controls")!;
const planCeilingSlider = document.getElementById("plan-ceiling-slider") as HTMLInputElement;
const planCeilingValue = document.getElementById("plan-ceiling-value")!;

// Viewer actions (in toolbar, right-aligned)
const toolbarViewerActions = document.getElementById("toolbar-viewer-actions")!;
const btnStreetview = document.getElementById("btn-streetview")!;
const btnPano360 = document.getElementById("btn-pano360")! as HTMLButtonElement;
const btnPanoAlign = document.getElementById("btn-pano-align")! as HTMLButtonElement;
const btnPanoBack = document.getElementById("btn-pano-back")! as HTMLButtonElement;
const btnMinimap = document.getElementById("btn-minimap")!;
const btnFloorplan = document.getElementById("btn-floorplan")!;
const btnToggleMeasures = document.getElementById("btn-toggle-measures")!;
const btnToggleNotes = document.getElementById("btn-toggle-notes")!;
const btnScreenshot = document.getElementById("btn-screenshot")!;
const btnSidePanel = document.getElementById("btn-side-panel")!;
const btnRoomplan = document.getElementById("btn-roomplan")! as HTMLButtonElement;
const btnAlign = document.getElementById("btn-align")! as HTMLButtonElement;

// Align toolbar
const alignToolbar = document.getElementById("align-toolbar")!;
const alignHint = document.getElementById("align-hint")!;
const alignUndo = document.getElementById("align-undo")!;
const alignReset = document.getElementById("align-reset")!;
const alignApply = document.getElementById("align-apply")! as HTMLButtonElement;
const alignCancel = document.getElementById("align-cancel")!;
const alignClear = document.getElementById("align-clear")! as HTMLButtonElement;

// Modals
const confirmBackdrop = document.getElementById("confirm-backdrop")!;
const confirmCancel = document.getElementById("confirm-cancel")!;
const confirmOk = document.getElementById("confirm-ok")!;
const toast = document.getElementById("toast")!;

// ── State ─────────────────────────────────────────────────────────────────────
let viewer: Viewer3D | null = null;
let sidePanel: SidePanel | null = null;
let measurePanel: MeasurePanel | null = null;
let notePanel: NotePanel | null = null;
let inspectorPanel: InspectorPanel | null = null;
let currentProject: Project | null = null;
let measurements: Measurement[] = [];
let notes: Note[] = [];
let measuresVisible = true;
let notesVisible = true;
let confirmCallback: (() => void) | null = null;

type PageName = "ic" | "ic-detail" | "viewer";

// Tracks the active inline 3D viewer mounted inside an IC project detail
// page (left pane). Set by `embed3D` and called whenever we leave
// `#page-ic-detail` so the viewer DOM is restored to `#page-viewer` and
// the Three.js renderer is disposed before the host element is unmounted.
let inlineViewerDispose: (() => void) | null = null;

// IC Projects screen — primary landing for office/QA/billing users.
// Lazily initialised on first show so we don't fetch IC projects on cold
// boot of standalone-only deployments.
let icPage: IcProjectsPage | null = null;
function ensureIcPage(): IcProjectsPage {
  if (icPage === null) {
    icPage = new IcProjectsPage({
      container: pageIc,
      toast: (msg, kind) => showToast(msg, kind === "error"),
      onOpen: (proj) => openIcProjectDetail(proj),
    });
  }
  return icPage;
}

function populateIcDetailToolbar(project: import("./types.ts").IcProject): void {
  btnIcDetailBack.style.display = "";
  const a = project.assignment;
  icDetailTitle.innerHTML = "";
  const col = document.createElement("div");
  col.style.cssText =
    "display:flex;flex-direction:column;align-items:center;gap:1px;min-width:0;max-width:100%;";
  const titleEl = document.createElement("div");
  titleEl.style.cssText =
    "font-weight:700;font-size:14px;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;";
  titleEl.textContent =
    a.client_project_id || `Project ${project.project_id.slice(0, 8)}`;
  col.appendChild(titleEl);
  const subtitle = [a.address.line1, a.address.city, a.address.state]
    .filter(Boolean)
    .join(", ");
  if (subtitle) {
    const subEl = document.createElement("div");
    subEl.style.cssText =
      "font-size:11px;line-height:1.2;color:var(--text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;";
    subEl.textContent = subtitle;
    col.appendChild(subEl);
  }
  icDetailTitle.appendChild(col);
}

function clearIcDetailToolbar(): void {
  btnIcDetailBack.style.display = "none";
  icDetailTitle.innerHTML = "";
}

/** Move #page-viewer DOM into `host` and run `init`. Returns dispose. */
async function _mountInlineViewer(
  host: HTMLElement,
  init: () => Promise<void>,
): Promise<() => void> {
  const prevPosition = host.style.position;
  const prevOverflow = host.style.overflow;
  host.style.position = "relative";
  host.style.overflow = "hidden";

  const movedNodes: Node[] = Array.from(pageViewer.childNodes);
  for (const n of movedNodes) host.appendChild(n);

  try {
    await init();
  } catch (err) {
    for (const n of movedNodes) pageViewer.appendChild(n);
    host.style.position = prevPosition;
    host.style.overflow = prevOverflow;
    throw err;
  }

  toolbarViewerActions.style.display = "flex";

  let disposed = false;
  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    if (inlineViewerDispose === dispose) inlineViewerDispose = null;
    toolbarViewerActions.style.display = "none";
    teardownViewer();
    for (const n of movedNodes) pageViewer.appendChild(n);
    host.style.position = prevPosition;
    host.style.overflow = prevOverflow;
  };
  inlineViewerDispose = dispose;
  return dispose;
}

// IC Project detail — recreated on every navigation so the manifest is
// refreshed and per-project state doesn't leak across opens. The page owns
// its own DOM, so we don't keep a reference after construction.
function openIcProjectDetail(
  project: import("./types.ts").IcProject,
  { pushHistory = true }: { pushHistory?: boolean } = {},
): void {
  if (pushHistory) {
    const target = "?ic=" + encodeURIComponent(project.project_id);
    if (window.location.search !== target) {
      window.history.pushState({ ic: project.project_id }, "", target);
    }
  }
  populateIcDetailToolbar(project);
  new IcProjectDetailPage({
    container: pageIcDetail,
    project,
    toast: (msg, kind) => showToast(msg, kind === "error"),
    onBack: () => {
      window.history.pushState(null, "", window.location.pathname);
      showPage("ic");
      ensureIcPage().refresh();
    },
    embed3D: async (localProjectId, host) => {
      const localProject = await api.projects.get(localProjectId);
      return _mountInlineViewer(host, () => openProject(localProject));
    },
    embed3DFromUrl: async (meshUrl, host, opts) => {
      const format: "glb" | "ply" | "usdz" = meshUrl.toLowerCase().endsWith(".ply")
        ? "ply"
        : meshUrl.toLowerCase().endsWith(".usdz")
          ? "usdz"
          : "glb";
      return _mountInlineViewer(host, () =>
        openEmbed(
          {
            meshUrl,
            meshFormat: format,
            projectId: opts?.localProjectId,
            icProjectId: opts?.icProjectId,
          },
          { inline: true },
        ),
      );
    },
  });
  showPage("ic-detail");
}

/** Open an IC project by ID — fetches metadata then navigates to the detail page. */
async function openIcProjectById(projectId: string, opts?: { pushHistory?: boolean }): Promise<void> {
  try {
    const resp = await api.ic.getProject(projectId);
    openIcProjectDetail(resp.project, opts);
  } catch {
    showPage("ic");
    ensureIcPage().refresh();
  }
}

// ── Navigation ────────────────────────────────────────────────────────────────
function showPage(name: PageName): void {
  // Leaving #page-ic-detail invalidates any inline-mounted viewer, since
  // the host element will no longer be visible. Dispose first so the
  // viewer DOM is moved back into #page-viewer and the renderer is freed
  // before the destination page is shown.
  if (name !== "ic-detail" && inlineViewerDispose) {
    inlineViewerDispose();
    inlineViewerDispose = null;
  }
  // Clear the IC-detail toolbar slots when navigating away from the
  // detail page so the back button + title don't bleed into other pages.
  if (name !== "ic-detail") clearIcDetailToolbar();

  pageIc.classList.toggle("active", name === "ic");
  pageIcDetail.classList.toggle("active", name === "ic-detail");
  pageViewer.classList.toggle("active", name === "viewer");

  // Show back button and viewer actions only in Viewer
  viewerBack.style.display = name === "viewer" ? "" : "none";
  // Inline-mounted IC viewer manages toolbarViewerActions itself; only
  // toggle here when we're actually entering/leaving #page-viewer.
  if (name === "viewer") toolbarViewerActions.style.display = "flex";
  else if (!inlineViewerDispose) toolbarViewerActions.style.display = "none";

  // Highlight active nav button
  btnIcProjects.classList.toggle("active", name === "ic" || name === "ic-detail");
}

btnIcProjects.addEventListener("click", () => {
  showPage("ic");
  teardownViewer();
  ensureIcPage().refresh();
});
btnIcDetailBack.addEventListener("click", () => {
  // Mirrors the IC detail page's internal back action. inlineViewerDispose
  // (if any) is invoked by showPage("ic") below.
  window.history.pushState(null, "", window.location.pathname);
  showPage("ic");
  ensureIcPage().refresh();
});

window.addEventListener("popstate", () => {
  const p = new URLSearchParams(window.location.search).get("ic");
  if (p && getToken()) {
    openIcProjectById(p, { pushHistory: false });
  } else {
    showPage("ic");
    ensureIcPage().refresh();
  }
});
viewerBack.addEventListener("click", () => {
  showPage("ic");
  teardownViewer();
  ensureIcPage().refresh();
});

// ── Mode buttons ──────────────────────────────────────────────────────────────
function setActiveMode(mode: CameraMode): void {
  [modeDollhouse, modeWalkthrough, modeFloorplan].forEach((b) => b.classList.remove("active"));
  ({ dollhouse: modeDollhouse, walkthrough: modeWalkthrough, floorplan: modeFloorplan })[mode]
    .classList.add("active");
}

modeDollhouse.addEventListener("click", async () => {
  await viewer?.setMode("dollhouse");
  setActiveMode("dollhouse");
  _syncSectionSlider();
});
modeWalkthrough.addEventListener("click", async () => {
  await viewer?.setMode("walkthrough");
  setActiveMode("walkthrough");
  _syncSectionSlider();
  showToast("Click canvas to enable mouse look · WASD to move");
});
modeFloorplan.addEventListener("click", async () => {
  await viewer?.setMode("floorplan");
  setActiveMode("floorplan");
  // Hide clip toolbar (not relevant in plan mode)
  if (viewer?.isClipModeActive) {
    viewer.setClipModeActive(false);
    btnClip.classList.remove("active");
    clipToolbar.classList.remove("visible");
    [clipX, clipY, clipZ].forEach((b) => b.classList.remove("active"));
  }
  // Always show section slider in plan mode
  planControls.classList.add("visible");
  planCeilingSlider.value = "85";
  planCeilingValue.textContent = "85%";
});

/** Show/hide the section slider based on current state. */
function _syncSectionSlider(): void {
  const show = viewer?.isClipModeActive ?? false;
  planControls.classList.toggle("visible", show);
}

// ── Clip plane mode ──────────────────────────────────────────────────────────
btnClip.addEventListener("click", () => {
  if (!viewer) return;
  const newState = !viewer.isClipModeActive;
  viewer.setClipModeActive(newState);
  btnClip.classList.toggle("active", newState);
  clipToolbar.classList.toggle("visible", newState);
  planControls.classList.toggle("visible", newState);
  if (!newState) {
    [clipX, clipY, clipZ].forEach((b) => b.classList.remove("active"));
    // Reset section slider and clear Y clip
    viewer.clearSectionHeight();
    planCeilingSlider.value = "100";
    planCeilingValue.textContent = "100%";
  } else {
    // Auto-enable Y axis so the slider works immediately
    if (!viewer.getClipAxisState("y").enabled) {
      viewer.toggleClipAxis("y");
    }
    _syncClipAxisButtons();
  }
});

function _syncClipAxisButtons(): void {
  if (!viewer) return;
  for (const [btn, axis] of [[clipX, "x"], [clipY, "y"], [clipZ, "z"]] as [HTMLElement, ClipAxis][]) {
    btn.classList.toggle("active", viewer.getClipAxisState(axis).enabled);
  }
}

[
  [clipX, "x"],
  [clipY, "y"],
  [clipZ, "z"],
].forEach(([btn, axis]) => {
  (btn as HTMLElement).addEventListener("click", () => {
    if (!viewer) return;
    viewer.toggleClipAxis(axis as ClipAxis);
    _syncClipAxisButtons();
  });
});

// ── Section slider ──────────────────────────────────────────────────────────
planCeilingSlider.addEventListener("input", () => {
  if (!viewer) return;
  const pct = Number(planCeilingSlider.value);
  planCeilingValue.textContent = pct + "%";
  if (pct >= 100) {
    viewer.clearSectionHeight();
  } else {
    viewer.setSectionHeight(pct);
  }
});

// ── Measure toolbar ───────────────────────────────────────────────────────────
btnMeasure.addEventListener("click", () => {
  // Close notes toolbar if open
  notesToolbar.classList.remove("visible");
  btnNotes.classList.remove("active");
  stopNote();

  measureToolbar.classList.toggle("visible");
  btnMeasure.classList.toggle("active");
  if (!measureToolbar.classList.contains("visible")) stopMeasure();
});

[
  [mDistance, "distance"],
  [mHeight, "height"],
  [mArea, "area"],
  [mPoint, "point"],
].forEach(([btn, type]) => {
  (btn as HTMLElement).addEventListener("click", () => startMeasure(type as MeasurementType));
});

function startMeasure(type: MeasurementType): void {
  if (!viewer) return;
  viewer.startMeasure(type);
  [mDistance, mHeight, mArea, mPoint].forEach((b) => b.classList.remove("active"));
  const map: Record<MeasurementType, HTMLElement> = {
    distance: mDistance, height: mHeight, area: mArea, point: mPoint,
  };
  map[type].classList.add("active");
  const hints: Record<MeasurementType, string> = {
    distance: "Click 2 points · ESC cancel",
    height: "Click bottom then top · ESC cancel",
    area: "Click points · double-click / Enter / right-click to finish · ESC cancel",
    point: "Click a point · ESC cancel",
  };
  measureHint.textContent = hints[type];
}

function stopMeasure(): void {
  viewer?.stopMeasure();
  [mDistance, mHeight, mArea, mPoint].forEach((b) => b.classList.remove("active"));
}

// ── Notes toolbar ────────────────────────────────────────────────────────────
btnNotes.addEventListener("click", () => {
  // Close measure toolbar if open
  measureToolbar.classList.remove("visible");
  btnMeasure.classList.remove("active");
  stopMeasure();

  notesToolbar.classList.toggle("visible");
  btnNotes.classList.toggle("active");
  if (!notesToolbar.classList.contains("visible")) stopNote();
});

[
  [nRoom, "room"],
  [nNote, "note"],
  [nDamage, "damage"],
].forEach(([btn, cat]) => {
  (btn as HTMLElement).addEventListener("click", () => startNote(cat as NoteCategory));
});

function startNote(category: NoteCategory): void {
  if (!viewer) return;
  viewer.startNote(category);
  [nRoom, nNote, nDamage].forEach((b) => b.classList.remove("active"));
  const map: Record<NoteCategory, HTMLElement> = { room: nRoom, note: nNote, damage: nDamage };
  map[category].classList.add("active");
  const hints: Record<NoteCategory, string> = {
    room: "Click on model to mark a room · ESC cancel",
    note: "Click on model to add a note · ESC cancel",
    damage: "Click on model to mark damage · ESC cancel",
  };
  notesHint.textContent = hints[category];
}

function stopNote(): void {
  viewer?.stopNote();
  [nRoom, nNote, nDamage].forEach((b) => b.classList.remove("active"));
}

// Loads floor plan into viewer: prefers rooms_data JSON, falls back to SVG.
// Returns true if a floor plan was successfully loaded.
async function loadProjectFloorPlan(p: Pick<Project, "id" | "has_rooms_data" | "has_floorplan">): Promise<boolean> {
  if (!viewer) return false;
  if (p.has_rooms_data) {
    try {
      const json = await api.projects.assetJson<IcRoomPlanJson>(p.id, "rooms_data");
      viewer.loadFloorPlanFromJson(json);
      return true;
    } catch { /* fall through to SVG */ }
  }
  if (p.has_floorplan) {
    const fpUrl = await api.projects.assetBlobUrl(p.id, "floorplan");
    await viewer.loadFloorPlan(fpUrl);
    return true;
  }
  return false;
}

// ── Floor plan toggle ─────────────────────────────────────────────────────────
btnFloorplan.addEventListener("click", async () => {
  if (!viewer) return;

  // Already loaded — just toggle
  if (viewer.hasFloorPlan) {
    viewer.toggleFloorPlan();
    btnFloorplan.classList.toggle("active", viewer.floorPlanOverlayVisible);
    return;
  }

  if (!currentProject) {
    showToast("Floor plan not available.");
    return;
  }

  // Not loaded yet — check if it's ready on the server
  try {
    const p = await api.projects.get(currentProject.id);
    const loaded = await loadProjectFloorPlan(p);
    if (loaded) {
      viewer.toggleFloorPlan();
      btnFloorplan.classList.toggle("active", viewer.floorPlanOverlayVisible);
      showToast("Floor plan loaded.");
    } else {
      showToast("Floor plan not available for this project.");
    }
  } catch {
    showToast("Could not load floor plan.", true);
  }
});

// ── Minimap toggle ───────────────────────────────────────────────────────────
btnMinimap.addEventListener("click", async () => {
  if (!viewer) return;

  // If floor plan not yet loaded, try to fetch it (only when backed by a project)
  if (!viewer.hasFloorPlan && !currentProject) {
    showToast("Minimap requires a floor plan.", true);
    return;
  }
  if (!viewer.hasFloorPlan && currentProject) {
    try {
      const p = await api.projects.get(currentProject.id);
      const loaded = await loadProjectFloorPlan(p);
      if (loaded) {
        showToast("Floor plan loaded.");
      } else {
        showToast("Floor plan not available for this project.", true);
        return;
      }
    } catch {
      showToast("Could not load floor plan.", true);
      return;
    }
  }

  if (!viewer.hasFloorPlan) return;
  viewer.setMinimapVisible(!viewer.minimapVisible);
  btnMinimap.classList.toggle("active", viewer.minimapVisible);
});

// ── Streetview toggle ────────────────────────────────────────────────────────
let streetviewMarkersOn = false;
btnStreetview.addEventListener("click", () => {
  if (!viewer) return;
  if (!viewer.hasStreetView) {
    showToast("Street View is not available for this project. Upload a streetview .zip to enable.", true);
    return;
  }
  if (viewer.isStreetViewActive) {
    // Panel/panorama is open → close it and hide markers
    viewer.exitStreetView();
    streetviewMarkersOn = false;
    viewer.setStreetViewMarkersVisible(false);
    btnStreetview.classList.remove("active");
  } else if (streetviewMarkersOn) {
    // Markers visible, panel closed → hide markers
    streetviewMarkersOn = false;
    viewer.setStreetViewMarkersVisible(false);
    btnStreetview.classList.remove("active");
  } else {
    // Markers hidden → show markers (click on one opens panel).
    // Hide the other layer's markers so the floor plan isn't cluttered.
    if (pano360MarkersOn) {
      pano360MarkersOn = false;
      viewer.setPano360MarkersVisible(false);
      btnPano360.classList.remove("active");
    }
    streetviewMarkersOn = true;
    viewer.setStreetViewMarkersVisible(true);
    btnStreetview.classList.add("active");
  }
});

// ── 360° panorama button — dual-layer (equirect Insta360 + legacy pinhole) ──
let pano360MarkersOn = false;

function exitPanoUI(): void {
  btnPano360.classList.remove("active");
  btnPanoBack.style.display = "none";
  pano360MarkersOn = false;
  viewer?.setPano360MarkersVisible(false);
}

btnPano360.addEventListener("click", () => {
  if (!viewer) return;

  // Equirect (Insta360) layer takes priority when available.
  if (viewer.hasPano360) {
    if (viewer.isPano360Active) {
      viewer.exitPano360();
      exitPanoUI();
    } else if (pano360MarkersOn) {
      pano360MarkersOn = false;
      viewer.setPano360MarkersVisible(false);
      btnPano360.classList.remove("active");
    } else {
      if (streetviewMarkersOn) {
        streetviewMarkersOn = false;
        viewer.setStreetViewMarkersVisible(false);
        btnStreetview.classList.remove("active");
      }
      pano360MarkersOn = true;
      viewer.setPano360MarkersVisible(true);
      btnPano360.classList.add("active");
    }
    return;
  }

  // Fallback: pinhole RTABmap pano (legacy direct-enter behaviour).
  if (viewer.hasStreetView) {
    if (viewer.isStreetViewPanoActive) {
      viewer.exitStreetView();
      exitPanoUI();
    } else {
      viewer.enterPanorama();
      btnPano360.classList.add("active");
      btnPanoBack.style.display = "";
    }
    return;
  }

  showToast("Panorama not available for this project.", true);
});

// ── 360° alignment toggle (Phase D) ──────────────────────────────────────────
btnPanoAlign.addEventListener("click", () => {
  if (!viewer || !viewer.hasPano360) {
    showToast("Alignment requires 360° panoramas to be loaded.", true);
    return;
  }
  const turnOn = !viewer.isPano360AlignActive;
  if (turnOn) {
    // Make sure pano360 markers are visible while we align.
    if (streetviewMarkersOn) {
      streetviewMarkersOn = false;
      viewer.setStreetViewMarkersVisible(false);
      btnStreetview.classList.remove("active");
    }
    pano360MarkersOn = true;
    viewer.setPano360MarkersVisible(true);
    btnPano360.classList.add("active");
    showToast("Drag pano markers to reposition · Click marker to set yaw");
  } else {
    // Leaving align mode — close any open pano editor.
    if (viewer.isPano360Active) viewer.exitPano360();
  }
  viewer.setPano360AlignActive(turnOn);
  btnPanoAlign.classList.toggle("active", turnOn);
});

btnPanoBack.addEventListener("click", () => {
  if (!viewer) return;
  if (viewer.isPano360Active) viewer.exitPano360();
  else viewer.exitStreetView();
  exitPanoUI();
});

// ── RoomPlan alignment (manual pair picking) ─────────────────────────────────
function _updateAlignHint(step: "pick_mesh" | "pick_plan", count: number): void {
  const nextIdx = count + 1;
  if (step === "pick_mesh") {
    alignHint.textContent = `Pair ${nextIdx} — click point on 3D mesh`;
  } else {
    alignHint.textContent = `Pair ${nextIdx} — click matching point on plan`;
  }
  alignApply.disabled = count < 2;
}

async function _ensureRoomPlanLoaded(): Promise<boolean> {
  if (!viewer || !currentProject) return false;
  if (viewer.hasRoomPlanLayer) return true;
  if (!currentProject.has_rooms_data) return false;
  try {
    const data = await api.projects.assetJson<RoomPlanData>(currentProject.id, "rooms_data");
    const glbUrl = currentProject.has_roomplan_model
      ? await api.projects.assetBlobUrl(currentProject.id, "roomplan_model")
      : undefined;
    await viewer.loadRoomPlan(data, glbUrl);
    return true;
  } catch {
    return false;
  }
}

function _exitAlignUI(): void {
  alignToolbar.classList.remove("visible");
  btnAlign.classList.remove("active");
  alignApply.disabled = true;
}

btnAlign.addEventListener("click", async () => {
  if (!viewer || !currentProject) return;
  if (alignToolbar.classList.contains("visible")) {
    // Toggle off — same as Cancel.
    viewer.cancelAlignment();
    _exitAlignUI();
    return;
  }
  if (!currentProject.has_floorplan) {
    showToast("Floor plan SVG is required for alignment.", true);
    return;
  }
  const ok = await _ensureRoomPlanLoaded();
  if (!ok) {
    showToast("RoomPlan data is not available for this project.", true);
    return;
  }
  // Make the layer visible so the user can see which RoomPlan is being aligned.
  if (!viewer.roomPlanLayerVisible) {
    viewer.toggleRoomPlanLayer();
    btnRoomplan.classList.toggle("active", viewer.roomPlanLayerVisible);
  }
  const started = viewer.startAlignment(_updateAlignHint);
  if (!started) {
    showToast("Could not start alignment — floor plan not loaded.", true);
    return;
  }
  alignApply.disabled = true;
  alignToolbar.classList.add("visible");
  btnAlign.classList.add("active");
  alignClear.style.display = currentProject.has_alignment ? "" : "none";
});

alignUndo.addEventListener("click", () => viewer?.undoAlignmentPair());
alignReset.addEventListener("click", () => viewer?.resetAlignmentPairs());
alignCancel.addEventListener("click", () => {
  viewer?.cancelAlignment();
  _exitAlignUI();
});

alignApply.addEventListener("click", async () => {
  if (!viewer || !currentProject) return;
  const result = viewer.finishAlignment();
  if (!result) {
    showToast("Need at least 2 pairs. Keep picking.", true);
    return;
  }
  try {
    const saved = await api.alignment.save(currentProject.id, {
      target: result.target,
      source: result.source,
      yaw: result.yaw,
      tx: result.tx,
      tz: result.tz,
      rmse_meters: result.rmse_meters,
      method: result.method,
    });
    viewer.setAlignmentTransform(saved);
    currentProject = { ...currentProject, has_alignment: true };
    _exitAlignUI();
    showToast(`Alignment saved (RMSE ${(result.rmse * 100).toFixed(1)} cm).`);
  } catch (err) {
    showToast(`Could not save alignment: ${err}`, true);
  }
});

alignClear.addEventListener("click", async () => {
  if (!viewer || !currentProject) return;
  try {
    await api.alignment.delete(currentProject.id);
    viewer.clearAlignment();
    currentProject = { ...currentProject, has_alignment: false };
    viewer.cancelAlignment();
    _exitAlignUI();
    showToast("Saved alignment cleared.");
  } catch (err) {
    showToast(`Could not clear alignment: ${err}`, true);
  }
});

// ── RoomPlan 3D layer ────────────────────────────────────────────────────────
btnRoomplan.addEventListener("click", async () => {
  if (!viewer || !currentProject) return;

  if (viewer.hasRoomPlanLayer) {
    viewer.toggleRoomPlanLayer();
    btnRoomplan.classList.toggle("active", viewer.roomPlanLayerVisible);
    return;
  }

  if (!currentProject.has_rooms_data) {
    showToast("RoomPlan data is not available for this project.", true);
    return;
  }

  try {
    const data = await api.projects.assetJson<RoomPlanData>(currentProject.id, "rooms_data");
    const glbUrl = currentProject.has_roomplan_model
      ? await api.projects.assetBlobUrl(currentProject.id, "roomplan_model")
      : undefined;
    await viewer.loadRoomPlan(data, glbUrl);
    viewer.toggleRoomPlanLayer();
    btnRoomplan.classList.toggle("active", viewer.roomPlanLayerVisible);
    showToast("RoomPlan layer loaded.");
  } catch (err) {
    showToast(`Could not load RoomPlan: ${err}`, true);
  }
});

// ── Screenshot ────────────────────────────────────────────────────────────────
btnScreenshot.addEventListener("click", () => {
  if (!viewer) return;
  const dataUrl = viewer.takeScreenshot();
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = `${currentProject?.name ?? "screenshot"}.jpg`;
  a.click();
});

// ── Visibility toggles ───────────────────────────────────────────────────────
btnToggleMeasures.addEventListener("click", () => {
  measuresVisible = !measuresVisible;
  viewer?.setMeasurementsVisible(measuresVisible);
  btnToggleMeasures.classList.toggle("active", measuresVisible);
});

btnToggleNotes.addEventListener("click", () => {
  notesVisible = !notesVisible;
  viewer?.setNotesVisible(notesVisible);
  btnToggleNotes.classList.toggle("active", notesVisible);
});

// ── Side panel ───────────────────────────────────────────────────────────────
btnSidePanel.addEventListener("click", () => {
  if (!sidePanel) return;
  if (sidePanel.isOpen) sidePanel.close();
  else sidePanel.open(sidePanel.activeTab ?? "measures");
});

async function deleteMeasurement(id: string): Promise<void> {
  await api.measurements.delete(id);
  viewer?.removeAnnotation(id);
  measurements = measurements.filter((m) => m.id !== id);
  measurePanel?.render(measurements);
}

async function deleteNote(id: string): Promise<void> {
  await api.notes.delete(id);
  viewer?.removeNoteAnnotation(id);
  notes = notes.filter((n) => n.id !== id);
  notePanel?.render(notes);
}

async function openProject(p: Project): Promise<void> {
  currentProject = p;
  measurements = [];
  notes = [];
  measuresVisible = true;
  notesVisible = true;
  loadingOverlay.style.display = "flex";
  loadingText.textContent = "Loading 3D model…";

  teardownViewer();

  modeWalkthrough.removeAttribute("disabled");
  modeFloorplan.removeAttribute("disabled");
  btnMeasure.removeAttribute("disabled");
  btnNotes.removeAttribute("disabled");
  btnToggleMeasures.classList.add("active");
  btnToggleNotes.classList.add("active");
  setActiveMode("dollhouse");
  viewer = new Viewer3D(canvasContainer);

  // Create unified side panel with tabs
  sidePanel = new SidePanel(document.getElementById("side-panel-container")!);
  const measuresPane = sidePanel.addTab("measures", "Measures");
  const notesPane = sidePanel.addTab("notes", "Notes");
  const inspectorPane = sidePanel.addTab("inspector", "Inspector", false);
  sidePanel.onTabChange = (tab) => {
    btnSidePanel.classList.toggle("active", tab !== null);
    // Pause video when switching away from inspector
    if (tab !== "inspector") inspectorPanel?.pause();
  };

  measurePanel = new MeasurePanel(measuresPane, deleteMeasurement, (id) => viewer?.highlightMeasurement(id));
  notePanel = new NotePanel(notesPane, deleteNote, (id) => viewer?.highlightNote(id));
  inspectorPanel = new InspectorPanel(inspectorPane);

  viewer.onNoteComplete = async (result) => {
    const pt = { x: result.point.x, y: result.point.y, z: result.point.z };
    try {
      const saved = await api.notes.create({
        project_id: p.id,
        category: result.category,
        text: result.text,
        point: pt,
      });
      viewer?.addNoteAnnotation(
        saved.id,
        saved.category,
        new THREE.Vector3(saved.point.x, saved.point.y, saved.point.z),
        saved.text,
      );
      notes.push(saved);
      notePanel?.render(notes);
    } catch (err) {
      showToast(`Could not save note: ${err}`, true);
    }
  };

  viewer.onMeasureComplete = async (result) => {
    const pts = result.points.map((v) => ({ x: v.x, y: v.y, z: v.z }));

    try {
      const saved = await api.measurements.create({
        project_id: p.id,
        type: result.type,
        points: pts,
        value: result.value,
        unit: result.unit,
      });
      viewer?.addAnnotation(
        saved.id,
        saved.type,
        saved.points.map((pt) => new THREE.Vector3(pt.x, pt.y, pt.z)),
        saved.value ?? 0,
        saved.unit ?? "",
        saved.label,
      );
      measurements.push(saved);
      measurePanel?.render(measurements);
    } catch (err) {
      showToast(`Could not save measurement: ${err}`, true);
    }
  };

  try {
    const meshRole = p.mesh_role ?? "glb";
    const meshUrl = await api.projects.assetBlobUrl(p.id, meshRole);
    await viewer.loadProject(meshUrl, undefined, meshRole);
    loadingOverlay.style.display = "none";
    void loadProjectFloorPlan(p); // non-blocking, non-fatal
    if (p.has_rooms_data) btnRoomplan.removeAttribute("disabled");
    if (p.has_rooms_data && p.has_floorplan) btnAlign.removeAttribute("disabled");
    if (p.has_alignment) {
      try {
        const align = await api.alignment.get(p.id);
        if (align) viewer.setAlignmentTransform(align);
      } catch {
        // non-fatal
      }
    }

    // Load existing measurements
    measurements = await api.measurements.list(p.id);
    for (const m of measurements) {
      viewer.addAnnotation(
        m.id, m.type,
        m.points.map((pt) => new THREE.Vector3(pt.x, pt.y, pt.z)),
        m.value ?? 0, m.unit ?? "", m.label,
      );
    }
    measurePanel?.render(measurements);

    // Load existing notes
    notes = await api.notes.list(p.id);
    for (const n of notes) {
      viewer.addNoteAnnotation(
        n.id, n.category,
        new THREE.Vector3(n.point.x, n.point.y, n.point.z),
        n.text,
      );
    }
    notePanel?.render(notes);

    // Try loading inspection evidence
    if (inspectorPanel && sidePanel) {
      try {
        const data = await api.inspection.load(p.id);
        inspectorPanel.load(data, (f) => api.inspection.mediaUrl(p.id, f));
        sidePanel.setTabVisible("inspector", true);
      } catch {
        // not available for this project
      }
    }

    // Streetview + pano360: independent layers, both auto-fetched from Drive.
    viewer.onPanoExit = () => exitPanoUI();
    viewer.onPano360Enter = () => {
      btnPano360.classList.add("active");
      btnPanoBack.style.display = "";
    };

    try {
      const manifestUrl = api.streetview.manifestUrl(p.id);
      const imageBaseUrl = `/api/projects/${p.id}/streetview/images`;
      await viewer.loadStreetView(manifestUrl, imageBaseUrl);
      btnStreetview.removeAttribute("disabled");
      btnPano360.removeAttribute("disabled"); // legacy pinhole-pano fallback
      console.info("[StreetView] loaded OK for project", p.id);
    } catch (err) {
      console.warn("[StreetView] not loaded:", err);
      fetch(api.streetview.diagnoseUrl(p.id))
        .then((r) => r.json())
        .then((d) => console.info("[StreetView] diagnose:", d))
        .catch((e) => console.warn("[StreetView] diagnose failed:", e));
    }

    try {
      const manifestUrl = api.pano360.manifestUrl(p.id);
      const imageBaseUrl = api.pano360.imageBaseUrl(p.id);
      await viewer.loadPano360(p.id, manifestUrl, imageBaseUrl);
      btnPano360.removeAttribute("disabled");
      btnPanoAlign.removeAttribute("disabled");
    } catch {
      // not available for this project
    }
  } catch (err) {
    loadingText.textContent = `Failed to load: ${err}`;
    showToast(String(err), true);
  }
}

function teardownViewer(): void {
  sidePanel?.dispose();
  sidePanel = null;
  measurePanel = null;
  notePanel = null;
  inspectorPanel = null;
  viewer?.dispose();
  viewer = null;
  currentProject = null;
  measurements = [];
  notes = [];
  measureToolbar.classList.remove("visible");
  notesToolbar.classList.remove("visible");
  btnMeasure.classList.remove("active");
  btnNotes.classList.remove("active");
  modeWalkthrough.setAttribute("disabled", "");
  modeFloorplan.setAttribute("disabled", "");
  btnMeasure.setAttribute("disabled", "");
  btnNotes.setAttribute("disabled", "");
  btnMinimap.classList.remove("active");
  btnFloorplan.classList.remove("active");
  btnRoomplan.classList.remove("active");
  btnRoomplan.setAttribute("disabled", "");
  btnAlign.classList.remove("active");
  btnAlign.setAttribute("disabled", "");
  alignToolbar.classList.remove("visible");
  btnStreetview.classList.remove("active");
  btnStreetview.setAttribute("disabled", "");
  btnPano360.setAttribute("disabled", "");
  btnPano360.classList.remove("active");
  btnPanoAlign.setAttribute("disabled", "");
  btnPanoAlign.classList.remove("active");
  btnPanoBack.style.display = "none";
  streetviewMarkersOn = false;
  pano360MarkersOn = false;
  btnToggleMeasures.classList.remove("active");
  btnToggleNotes.classList.remove("active");
  btnSidePanel.classList.remove("active");
  // Reset clip mode
  btnClip.classList.remove("active");
  clipToolbar.classList.remove("visible");
  [clipX, clipY, clipZ].forEach((b) => b.classList.remove("active"));
  planControls.classList.remove("visible");
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
confirmCancel.addEventListener("click", () => confirmBackdrop.classList.remove("open"));
confirmBackdrop.addEventListener("click", (e) => { if (e.target === confirmBackdrop) confirmBackdrop.classList.remove("open"); });
confirmOk.addEventListener("click", () => {
  confirmBackdrop.classList.remove("open");
  confirmCallback?.();
  confirmCallback = null;
});

// ── Toast ─────────────────────────────────────────────────────────────────────
let toastTimer: ReturnType<typeof setTimeout> | null = null;
function showToast(msg: string, error = false): void {
  toast.textContent = msg;
  toast.classList.toggle("error", error);
  toast.classList.add("show");
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove("show"), 4500);
}

// ── Embed mode ────────────────────────────────────────────────────────────────
// Activated when the page is loaded with `?embed=1` (or `?mesh=...`). The viewer
// loads a single .glb/.ply directly from the URL, with no project list, no Drive
// browser, no upload flow, and no backend persistence (measurements/notes live in
// memory only). A `?buttons=` allowlist controls which toolbar controls are shown.
//
// Supported URL params:
//   embed=1                — explicit toggle (optional if `mesh` is present)
//   mesh=<url>             — required: URL to the .glb / .ply
//   format=glb|ply         — optional, inferred from URL extension by default
//   floorplan=<url>        — optional: URL to floor plan SVG
//   buttons=k1,k2,...      — optional: only show these toolbar buttons; if omitted,
//                            all buttons are shown. Keys: dollhouse, walk, plan,
//                            modes (= all 3 modes), measure, notes, clip, pano360,
//                            panoalign, streetview, minimap, floorplan,
//                            togglemeasures, togglenotes, roomplan, align,
//                            screenshot, sidepanel
//   name=<string>          — optional: shown in the document title
//
// Example:
//   /viewer/?mesh=/assets/demo-scan.glb&buttons=modes,measure,minimap,screenshot

interface EmbedConfig {
  meshUrl: string;
  meshFormat: "glb" | "ply" | "usdz";
  floorPlanUrl?: string;
  allowedButtons?: Set<string>;
  name?: string;
  /** Local inspect_3d Project UUID. Used for streetview if no icProjectId. */
  projectId?: string;
  /** IC project id. Preferred for streetview when set — works without a
   *  local Project record. */
  icProjectId?: string;
}

/** Map of toolbar element id → set of short keys it satisfies. */
const EMBED_BUTTON_KEYS: Record<string, string[]> = {
  "mode-dollhouse": ["dollhouse", "modes"],
  "mode-walkthrough": ["walk", "walkthrough", "modes"],
  "mode-floorplan": ["plan", "modes"],
  "btn-measure": ["measure"],
  "btn-notes": ["notes"],
  "btn-clip": ["clip"],
  "btn-pano360": ["pano360", "pano"],
  "btn-pano-align": ["panoalign"],
  "btn-streetview": ["streetview"],
  "btn-minimap": ["minimap"],
  "btn-floorplan": ["floorplan"],
  "btn-toggle-measures": ["togglemeasures"],
  "btn-toggle-notes": ["togglenotes"],
  "btn-roomplan": ["roomplan"],
  "btn-align": ["align"],
  "btn-screenshot": ["screenshot"],
  "btn-side-panel": ["sidepanel"],
};

function _parseEmbedConfig(params: URLSearchParams): EmbedConfig | null {
  const meshUrl = params.get("mesh");
  if (!meshUrl) return null;
  const explicitFormat = params.get("format");
  const meshFormat: "glb" | "ply" | "usdz" = explicitFormat === "ply"
    ? "ply"
    : explicitFormat === "usdz"
      ? "usdz"
      : explicitFormat === "glb"
        ? "glb"
        : meshUrl.toLowerCase().endsWith(".ply")
          ? "ply"
          : meshUrl.toLowerCase().endsWith(".usdz")
            ? "usdz"
            : "glb";
  const floorPlanUrl = params.get("floorplan") ?? undefined;
  const buttonsParam = params.get("buttons");
  const allowedButtons = buttonsParam
    ? new Set(buttonsParam.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean))
    : undefined;
  const name = params.get("name") ?? undefined;
  return { meshUrl, meshFormat, floorPlanUrl, allowedButtons, name };
}

function _applyEmbedButtonAllowlist(allow: Set<string> | undefined): void {
  if (!allow) return;
  for (const [id, keys] of Object.entries(EMBED_BUTTON_KEYS)) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (!keys.some((k) => allow.has(k))) {
      el.style.display = "none";
    }
  }
  // Hide the in-toolbar separator if no measure/notes buttons survived.
  const sepHasNeighbours = !!(
    document.getElementById("btn-measure")?.style.display !== "none" ||
    document.getElementById("btn-notes")?.style.display !== "none"
  );
  if (!sepHasNeighbours) {
    const sep = document.querySelector<HTMLElement>("#viewer-toolbar > div[style*='width:1px']");
    if (sep) sep.style.display = "none";
  }
}

async function openEmbed(cfg: EmbedConfig, opts: { inline?: boolean } = {}): Promise<void> {
  currentProject = null;
  measurements = [];
  notes = [];
  measuresVisible = true;
  notesVisible = true;

  if (!opts.inline) {
    showPage("viewer");
    // No back button in embed (nothing to go back to).
    viewerBack.style.display = "none";
  }

  loadingOverlay.style.display = "flex";
  loadingText.textContent = "Loading 3D model…";

  teardownViewer();

  modeWalkthrough.removeAttribute("disabled");
  modeFloorplan.removeAttribute("disabled");
  btnMeasure.removeAttribute("disabled");
  btnNotes.removeAttribute("disabled");
  btnToggleMeasures.classList.add("active");
  btnToggleNotes.classList.add("active");
  setActiveMode("dollhouse");

  viewer = new Viewer3D(canvasContainer);

  sidePanel = new SidePanel(document.getElementById("side-panel-container")!);
  const measuresPane = sidePanel.addTab("measures", "Measures");
  const notesPane = sidePanel.addTab("notes", "Notes");
  sidePanel.onTabChange = (tab) => {
    btnSidePanel.classList.toggle("active", tab !== null);
  };

  measurePanel = new MeasurePanel(
    measuresPane,
    async (id) => {
      viewer?.removeAnnotation(id);
      measurements = measurements.filter((m) => m.id !== id);
      measurePanel?.render(measurements);
    },
    (id) => viewer?.highlightMeasurement(id),
  );
  notePanel = new NotePanel(
    notesPane,
    async (id) => {
      viewer?.removeNoteAnnotation(id);
      notes = notes.filter((n) => n.id !== id);
      notePanel?.render(notes);
    },
    (id) => viewer?.highlightNote(id),
  );

  // Memory-only persistence — no /api/measurements, no /api/notes.
  let nextEmbedId = 1;
  const genEmbedId = (): string => `embed-${nextEmbedId++}`;

  viewer.onMeasureComplete = (result) => {
    const id = genEmbedId();
    viewer?.addAnnotation(id, result.type, result.points, result.value, result.unit);
    measurements.push({
      id,
      project_id: "embed",
      type: result.type,
      points: result.points.map((v) => ({ x: v.x, y: v.y, z: v.z })),
      value: result.value,
      unit: result.unit,
      label: null,
      created_at: new Date().toISOString(),
    });
    measurePanel?.render(measurements);
  };

  viewer.onNoteComplete = (result) => {
    const id = genEmbedId();
    viewer?.addNoteAnnotation(id, result.category, result.point, result.text);
    notes.push({
      id,
      project_id: "embed",
      category: result.category,
      text: result.text,
      point: { x: result.point.x, y: result.point.y, z: result.point.z },
      created_at: new Date().toISOString(),
    });
    notePanel?.render(notes);
  };

  if (cfg.name) document.title = cfg.name;

  try {
    await viewer.loadProject(cfg.meshUrl, cfg.floorPlanUrl, cfg.meshFormat);
    loadingOverlay.style.display = "none";
  } catch (err) {
    loadingText.textContent = `Failed to load: ${err}`;
    showToast(String(err), true);
  }

  // Prefer the IC-keyed streetview endpoint when an IC id is available —
  // it works even without a local Project record (project-captures path).
  // imageUrlQuery is appended to per-image URLs so TextureLoader requests
  // authenticate via ?token=... (it can't set an Authorization header).
  const svManifestUrl = cfg.icProjectId
    ? api.icStreetview.manifestUrl(cfg.icProjectId)
    : cfg.projectId
      ? api.streetview.manifestUrl(cfg.projectId)
      : null;
  const svImageBaseUrl = cfg.icProjectId
    ? api.icStreetview.imageBaseUrl(cfg.icProjectId)
    : cfg.projectId
      ? `/api/projects/${cfg.projectId}/streetview/images`
      : null;
  const svImageUrlQuery = cfg.icProjectId ? api.icStreetview.tokenQuery() : "";
  const svDiagnoseUrl = cfg.icProjectId
    ? api.icStreetview.diagnoseUrl(cfg.icProjectId)
    : cfg.projectId
      ? api.streetview.diagnoseUrl(cfg.projectId)
      : null;
  const svKey = cfg.icProjectId ?? cfg.projectId;

  if (svManifestUrl && svImageBaseUrl) {
    viewer.onPanoExit = () => exitPanoUI();
    try {
      await viewer.loadStreetView(svManifestUrl, svImageBaseUrl, svImageUrlQuery);
      btnStreetview.removeAttribute("disabled");
      btnPano360.removeAttribute("disabled");
      console.info("[StreetView] loaded OK for project", svKey);
    } catch (err) {
      console.warn("[StreetView] not loaded:", err);
      if (svDiagnoseUrl) {
        fetch(svDiagnoseUrl)
          .then((r) => r.json())
          .then((d) => console.info("[StreetView] diagnose:", d))
          .catch((e) => console.warn("[StreetView] diagnose failed:", e));
      }
    }
  }
}

function _initEmbed(cfg: EmbedConfig): void {
  // Drop the entire 52px top header bar in embed mode — none of its contents
  // (brand, Projects/Drive nav, viewer-actions group) belong on a website embed.
  // Collapse the #app grid row so the viewer fills the full iframe.
  const toolbar = document.getElementById("toolbar");
  if (toolbar) toolbar.style.display = "none";
  const app = document.getElementById("app");
  if (app) app.style.gridTemplateRows = "1fr";

  _applyEmbedButtonAllowlist(cfg.allowedButtons);

  void openEmbed(cfg);
}

// ── Auth ──────────────────────────────────────────────────────────────────────
function _showLoginPage(): void {
  new LoginPage({
    onSuccess: () => {
      if (_embedCfg) {
        _initEmbed(_embedCfg);
      } else {
        showPage("ic");
        ensureIcPage().refresh();
      }
    },
  });
}

btnLogout.addEventListener("click", async () => {
  try {
    await api.auth.logout();
  } catch {
    // Non-fatal — clear locally regardless
  }
  clearToken();
  _showLoginPage();
});

window.addEventListener("auth:unauthorized", () => {
  // Avoid stacking multiple login overlays
  if (document.getElementById("login-overlay")) return;
  _showLoginPage();
});

// ── Init ──────────────────────────────────────────────────────────────────────
const _params = new URLSearchParams(window.location.search);
const _embedCfg = _params.get("embed") === "1" || _params.has("mesh")
  ? _parseEmbedConfig(_params)
  : null;

if (!getToken()) {
  _showLoginPage();
} else if (_embedCfg) {
  _initEmbed(_embedCfg);
} else {
  const _icProjectId = _params.get("ic");
  showPage("ic");
  ensureIcPage().refresh();
  if (_icProjectId) {
    openIcProjectById(_icProjectId, { pushHistory: false });
  }
}
